import Request from "libs/request";
import { location } from "libs/window";
import camelCase from "lodash-es/camelCase";
import mapKeys from "lodash-es/mapKeys";
import type {
  AdditionalCoordinate,
  AiModel,
  APIAnnotationType,
  APIJob,
  RenderAnimationOptions,
  VoxelSize,
} from "types/api_types";
import type { Vector3, Vector6 } from "viewer/constants";
import { setActiveOrganizationsCreditBalance } from "viewer/model/actions/organization_actions";
import { Store } from "viewer/singletons";
import { assertResponseLimit } from "./api_utils";
import { getOrganization } from "./organization";

function transformBackendJobToAPIJob(job: any): APIJob {
  return {
    ...job,
    args: mapKeys(job.args, (_value, key) => camelCase(key)),
  };
}

export async function getJobs(
  command?: string,
  skipForDeletedDatasets?: boolean,
): Promise<APIJob[]> {
  const params = new URLSearchParams();
  if (command) {
    params.set("command", command);
  }
  if (skipForDeletedDatasets != null) {
    params.set("skipForDeletedDatasets", skipForDeletedDatasets ? "true" : "false");
  }

  const jobs = await Request.receiveJSON(`/api/jobs?${params}`);
  assertResponseLimit(jobs);
  return jobs.map(transformBackendJobToAPIJob);
}

export async function getJob(jobId: string): Promise<APIJob> {
  const job = await Request.receiveJSON(`/api/jobs/${jobId}`);
  return transformBackendJobToAPIJob(job);
}

export async function cancelJob(jobId: string): Promise<APIJob> {
  return Request.receiveJSON(`/api/jobs/${jobId}/cancel`, {
    method: "PATCH",
  });
}

export type JobCreditCostInfo = {
  costInMilliCredits: number;
  hasEnoughCredits: boolean;
  // The organizations credits used during calculation whether the organization has enough credits for the job.
  organizationMilliCredits: number;
};

async function getJobCreditCost(
  command: string,
  boundingBoxInMag: Vector6,
): Promise<JobCreditCostInfo> {
  const params = new URLSearchParams({
    command,
    boundingBoxInMag: boundingBoxInMag.join(","),
  });
  return await Request.receiveJSON(`/api/jobs/getCreditCost?${params}`);
}

export async function getJobCreditCostAndUpdateOrgaCredits(
  command: string,
  boundingBoxInMag: Vector6,
): Promise<JobCreditCostInfo> {
  const jobCreditCostInfo = await getJobCreditCost(command, boundingBoxInMag);
  Store.dispatch(setActiveOrganizationsCreditBalance(jobCreditCostInfo.organizationMilliCredits));
  return jobCreditCostInfo;
}

export async function refreshOrganizationCredits() {
  const organizationId = Store.getState().activeOrganization?.id;
  if (organizationId) {
    const orga = await getOrganization(organizationId);
    if (orga.milliCreditBalance != null) {
      Store.dispatch(setActiveOrganizationsCreditBalance(orga.milliCreditBalance));
    }
  }
}

export async function retryJob(jobId: string): Promise<APIJob> {
  return Request.receiveJSON(`/api/jobs/${jobId}/retry`, {
    method: "PATCH",
  });
}

export async function startFindLargestSegmentIdJob(
  datasetId: string,
  layerName: string,
): Promise<APIJob> {
  return Request.receiveJSON(
    `/api/jobs/run/findLargestSegmentId/${datasetId}?layerName=${layerName}`,
    {
      method: "POST",
    },
  );
}

export async function startExportTiffJob(
  datasetId: string,
  bbox: Vector6,
  additionalCoordinates: AdditionalCoordinate[] | null,
  layerName: string | null | undefined,
  mag: string | null | undefined,
  annotationId: string | null | undefined,
  annotationLayerName: string | null | undefined,
  asOmeTiff: boolean,
): Promise<APIJob> {
  const params = new URLSearchParams({ bbox: bbox.join(","), asOmeTiff: asOmeTiff.toString() });
  if (layerName != null) {
    params.append("layerName", layerName);
  }
  if (mag != null) {
    params.append("mag", mag);
  }
  if (annotationId != null) {
    params.append("annotationId", annotationId);
  }
  if (annotationLayerName != null) {
    params.append("annotationLayerName", annotationLayerName);
  }
  if (additionalCoordinates != null) {
    params.append("additionalCoordinates", JSON.stringify(additionalCoordinates));
  }
  return Request.receiveJSON(`/api/jobs/run/exportTiff/${datasetId}?${params}`, {
    method: "POST",
  });
}

export function startComputeMeshFileJob(
  datasetId: string,
  layerName: string,
  mag: Vector3,
  agglomerateView?: string,
): Promise<APIJob> {
  const params = new URLSearchParams();
  params.append("layerName", layerName);
  params.append("mag", mag.join("-"));

  if (agglomerateView) {
    params.append("agglomerateView", agglomerateView);
  }

  return Request.receiveJSON(`/api/jobs/run/computeMeshFile/${datasetId}?${params}`, {
    method: "POST",
  });
}

export function startComputeSegmentIndexFileJob(
  datasetId: string,
  layerName: string,
): Promise<APIJob> {
  const params = new URLSearchParams();
  params.append("layerName", layerName);

  return Request.receiveJSON(`/api/jobs/run/computeSegmentIndexFile/${datasetId}?${params}`, {
    method: "POST",
  });
}

export async function getAiModelVoxelSize(aiModelId: string): Promise<VoxelSize> {
  return Request.receiveJSON(`/api/aiModels/${aiModelId}/voxelSize`);
}

type RunNeuronInferenceParameters = {
  datasetId: string;
  aiModelId?: string;
  colorLayerName: string;
  boundingBox: string;
  annotationId?: string;
  maskAnnotationLayerName?: string;
  newDatasetName: string;
  workflowYaml?: string;
  doSplitMergerEvaluation: boolean;
  evalUseSparseTracing?: boolean;
  evalMaxEdgeLength?: number;
  evalSparseTubeThresholdNm?: number;
  evalMinMergerPathLengthNm?: number;
  customConfiguration?: Record<string, JsonValue>;
};

type RunInstanceInferenceParameters = {
  datasetId: string;
  aiModelId?: string;
  colorLayerName: string;
  boundingBox: string;
  annotationId?: string;
  maskAnnotationLayerName?: string;
  newDatasetName: string;
  workflowYaml?: string;
  seedGeneratorDistanceThreshold?: number | null;
  customConfiguration?: Record<string, JsonValue>;
};

export function runNeuronModelInference(params: RunNeuronInferenceParameters): Promise<APIJob> {
  return Request.sendJSONReceiveJSON("/api/aiModels/inferences/runNeuronModelInference", {
    method: "POST",
    data: params,
  });
}

export function runInstanceModelInference(params: RunInstanceInferenceParameters): Promise<APIJob> {
  return Request.sendJSONReceiveJSON("/api/aiModels/inferences/runInstanceModelInference", {
    method: "POST",
    data: params,
  });
}

export function startRenderAnimationJob(
  datasetId: string,
  animationOptions: RenderAnimationOptions,
): Promise<APIJob> {
  return Request.sendJSONReceiveJSON(`/api/jobs/run/renderAnimation/${datasetId}`, {
    data: animationOptions,
  });
}

function startSegmentationAnnotationDependentJob(
  jobURLPath: string,
  datasetId: string,
  fallbackLayerName: string,
  volumeLayerName: string | null | undefined,
  newDatasetName: string,
  annotationId: string,
  annotationType: APIAnnotationType,
  mergeSegments?: boolean,
  includesEditableMapping?: boolean,
  boundingBox?: Vector6,
): Promise<APIJob> {
  const requestURL = new URL(`/api/jobs/run/${jobURLPath}/${datasetId}`, location.origin);
  if (volumeLayerName != null) {
    requestURL.searchParams.append("volumeLayerName", volumeLayerName);
  }
  const layerName = volumeLayerName || fallbackLayerName;
  requestURL.searchParams.append("fallbackLayerName", fallbackLayerName);
  requestURL.searchParams.append("annotationId", annotationId);
  requestURL.searchParams.append("annotationType", annotationType);
  requestURL.searchParams.append("newDatasetName", newDatasetName);
  requestURL.searchParams.append("outputSegmentationLayerName", `${layerName}_materialized`);
  if (mergeSegments != null) {
    requestURL.searchParams.append("mergeSegments", mergeSegments.toString());
  }
  if (includesEditableMapping != null) {
    requestURL.searchParams.append("includesEditableMapping", includesEditableMapping.toString());
  }
  if (boundingBox) {
    requestURL.searchParams.append("boundingBox", boundingBox.join(","));
  }
  return Request.receiveJSON(requestURL.href, {
    method: "POST",
  });
}

export function startMaterializingVolumeAnnotationJob(
  datasetId: string,
  fallbackLayerName: string,
  volumeLayerName: string | null | undefined,
  newDatasetName: string,
  annotationId: string,
  annotationType: APIAnnotationType,
  mergeSegments: boolean,
  includesEditableMapping: boolean,
  boundingBox?: Vector6,
): Promise<APIJob> {
  return startSegmentationAnnotationDependentJob(
    "materializeVolumeAnnotation",
    datasetId,
    fallbackLayerName,
    volumeLayerName,
    newDatasetName,
    annotationId,
    annotationType,
    mergeSegments,
    includesEditableMapping,
    boundingBox,
  );
}

export function runPretrainedMitochondriaInferenceJob(
  datasetId: string,
  layerName: string,
  bbox: Vector6,
  newDatasetName: string,
): Promise<APIJob> {
  const urlParams = new URLSearchParams({
    layerName,
    bbox: bbox.join(","),
    newDatasetName,
  });
  return Request.receiveJSON(
    `/api/jobs/run/inferMitochondria/${datasetId}?${urlParams.toString()}`,
    {
      method: "POST",
    },
  );
}

export function startAlignSectionsJob(
  datasetId: string,
  layerName: string,
  newDatasetName: string,
  annotationId?: string,
  customConfiguration?: Record<string, JsonValue>,
): Promise<APIJob> {
  return Request.sendJSONReceiveJSON(`/api/jobs/run/alignSections/${datasetId}`, {
    method: "POST",
    data: { layerName, newDatasetName, annotationId, customConfiguration },
  });
}

// This enum needs to be kept in sync with the backend/database
export enum APIAiModelCategory {
  EM_NEURONS = "em_neurons",
  EM_NUCLEI = "em_nuclei",
  EM_SYNAPSES = "em_synapses",
  EM_NEURON_TYPES = "em_neuron_types",
  EM_CELL_ORGANELLES = "em_cell_organelles",
  EM_GENERIC = "em_generic",
  EM_SOMATA = "em_somata",
  EM_MITOCHONDRIA = "em_mitochondria",
}

export type AiModelTrainingAnnotationSpecification = {
  annotationId: string;
  colorLayerName: string;
  segmentationLayerName: string;
  mag: Vector3;
};

type RunNeuronModelTrainingParameters = {
  trainingAnnotations: AiModelTrainingAnnotationSpecification[];
  name: string;
  aiModelCategory: APIAiModelCategory.EM_NEURONS;
  comment?: string;
  workflowYaml?: string;
  customConfiguration?: Record<string, JsonValue>;
};

export function runNeuronTraining(params: RunNeuronModelTrainingParameters) {
  return Request.sendJSONReceiveJSON("/api/aiModels/runNeuronModelTraining", {
    method: "POST",
    data: JSON.stringify(params),
  });
}

export type JsonPrimitive = string | number | boolean;
// A configuration value may also be a list of values or a list of value groups
// (e.g. a set of coordinates or bounding boxes).
export type JsonValue = JsonPrimitive | JsonValue[];

type RunInstanceModelTrainingParameters = {
  trainingAnnotations: AiModelTrainingAnnotationSpecification[];
  name: string;
  aiModelCategory: APIAiModelCategory.EM_GENERIC;
  instanceDiameterNm: number;
  comment?: string;
  workflowYaml?: string;
  customConfiguration?: Record<string, JsonValue>;
};

export function runInstanceModelTraining(params: RunInstanceModelTrainingParameters) {
  return Request.sendJSONReceiveJSON("/api/aiModels/runInstanceModelTraining", {
    method: "POST",
    data: JSON.stringify(params),
  });
}

export async function getAiModels(): Promise<AiModel[]> {
  const models = await Request.receiveJSON("/api/aiModels");
  return models.map((model: any) => ({
    ...model,
    trainingJob: model.trainingJob == null ? null : transformBackendJobToAPIJob(model.trainingJob),
  }));
}

export async function updateAiModel(aiModel: AiModel) {
  return Request.sendJSONReceiveJSON(`/api/aiModels/${aiModel.id}`, {
    method: "PUT",
    data: {
      name: aiModel.name,
      comment: aiModel.comment,
      sharedOrganizationIds: aiModel.sharedOrganizationIds,
    },
  });
}
